<template>
  <div class="kefu-page">
    <section class="conversation-panel">
      <div class="panel-title">
        <div>
          <strong>会话记录</strong>
          <span>({{ conversationList.length }})</span>
        </div>
        <div class="panel-actions">
          <el-button
            size="mini"
            type="primary"
            icon="el-icon-chat-dot-round"
            @click="openContactDialog"
            >联系用户</el-button
          >
          <el-button size="mini" icon="el-icon-refresh" @click="loadConversationList" />
        </div>
      </div>

      <div class="conversation-scopes">
        <el-radio-group v-model="conversationScope" size="mini" @change="handleScopeChange">
          <el-radio-button label="PENDING">待分配</el-radio-button>
          <el-radio-button label="MINE">我的会话</el-radio-button>
          <el-radio-button label="ALL">全部会话</el-radio-button>
          <el-radio-button label="ARCHIVED">已归档</el-radio-button>
        </el-radio-group>
      </div>

      <div v-loading="conversationLoading" class="conversation-list">
        <button
          v-for="item in conversationList"
          :key="item.id"
          type="button"
          class="conversation-item"
          :class="{ active: activeConversation && activeConversation.id === item.id }"
          @click="selectConversation(item)"
        >
          <el-image
            v-if="item.userAvatar"
            class="avatar avatar-image"
            :src="item.userAvatar"
            fit="cover"
          />
          <div v-else class="avatar">{{ getAvatarText(item.userNickname) }}</div>
          <div class="conversation-main">
            <div class="conversation-row">
              <strong>{{ item.userNickname || "访客" }}</strong>
              <time class="conversation-date">{{ formatShortDate(item.lastMessageTime) }}</time>
            </div>
            <div class="conversation-row last-message">
              <span>{{ formatLastMessage(item) }}</span>
              <span
                class="service-mode-dot"
                :class="
                  `service-mode-dot--${String(item.serviceMode || 'AI_ACTIVE').toLowerCase()}`
                "
                >{{ serviceModeShort(item.serviceMode) }}</span
              >
              <em v-if="item.adminUnreadMessageCount">{{ item.adminUnreadMessageCount }}</em>
            </div>
            <div v-if="item.assignedAdminName" class="assigned-agent">
              坐席：{{ item.assignedAdminName }}
            </div>
          </div>
        </button>

        <div v-if="!conversationLoading && conversationList.length === 0" class="empty-box">
          暂无会话，用户发起咨询后会显示在这里
        </div>
      </div>
    </section>

    <section class="chat-panel">
      <header class="chat-header">
        <div>
          <strong>{{ activeConversation ? activeConversation.userNickname : "请选择会话" }}</strong>
          <span v-if="activeConversation">用户ID：{{ activeConversation.userId }}</span>
          <el-tag
            v-if="activeConversation"
            size="mini"
            :type="serviceModeTagType(activeConversation.serviceMode)"
            >{{ serviceModeLabel(activeConversation.serviceMode) }}</el-tag
          >
        </div>
        <div v-if="activeConversation" class="header-actions">
          <template v-if="!activeConversation.archived">
            <el-button
              v-if="activeConversation.assignedAdminId !== currentAdminId"
              size="mini"
              type="primary"
              @click="takeoverConversation"
              >认领会话</el-button
            >
            <el-dropdown trigger="click" @command="assignConversation">
              <el-button size="mini"
                >{{ activeConversation.assignedAdminId ? "转派" : "指派"
                }}<i class="el-icon-arrow-down el-icon--right"
              /></el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item
                  v-for="agent in enabledAgents"
                  :key="agent.id"
                  :command="agent.id"
                  :disabled="agent.id === activeConversation.assignedAdminId"
                >
                  {{ agent.name || agent.account }}（{{ agent.activeConversationCount || 0 }}）
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
            <el-button
              v-if="activeConversation.serviceMode === 'HUMAN_ACTIVE'"
              size="mini"
              type="success"
              plain
              @click="returnConversationToAi"
              >交回 AI</el-button
            >
            <el-button size="mini" @click="archiveConversation">归档</el-button>
          </template>
          <el-button v-else size="mini" type="primary" @click="restoreConversation"
            >恢复会话</el-button
          >
          <el-button size="mini" @click="togglePinned">
            {{ activeConversation.adminPinned ? "取消置顶" : "置顶" }}
          </el-button>
          <el-button size="mini" type="danger" @click="removeConversation">删除</el-button>
        </div>
      </header>

      <main ref="messageBody" v-loading="messageLoading" class="message-body" @scroll="handleMessageScroll">
        <template v-if="activeConversation">
          <div v-if="messageHasMore" class="load-more-history">
            <el-button
              type="text"
              size="mini"
              :loading="loadingMoreMessages"
              @click="loadMoreMessages"
            >
              {{ loadingMoreMessages ? "加载中..." : "点击查看更多历史消息 (已加载 " + messageList.length + "/" + messageTotal + " 条)" }}
            </el-button>
          </div>
          <div v-else-if="messageList.length > 0 && messageList.length >= messagePageSize" class="no-more-history">
            <span>已加载全部历史消息 (共 {{ messageTotal || messageList.length }} 条)</span>
          </div>

          <div
            v-for="message in messageList"
            :key="message.id"
            class="message-row"
            :class="{
              mine: message.senderType === 1 || message.senderType === 3,
              system: message.senderType === 4
            }"
          >
            <el-image
              v-if="message.senderType === 2 && activeConversation.userAvatar"
              class="message-avatar message-avatar-image"
              :src="activeConversation.userAvatar"
              fit="cover"
            />
            <el-image
              v-else-if="
                (message.senderType === 1 ||
                  message.senderType === 3 ||
                  message.senderType === 4) &&
                  serviceAvatar
              "
              class="message-avatar message-avatar-image"
              :src="serviceAvatar"
              fit="cover"
            />
            <div v-else class="message-avatar">
              {{ message.senderType === 1 ? "客" : getAvatarText(activeConversation.userNickname) }}
            </div>
            <div class="message-bubble">
              <template v-if="message.contentType === contentType.RECALLED">
                <span class="recalled-message">{{
                  message.senderType === 1 ? "客服撤回了一条消息" : "用户撤回了一条消息"
                }}</span>
              </template>
              <template v-else-if="message.contentType === contentType.IMAGE">
                <el-image
                  class="message-image"
                  :src="parseImageUrl(message.content)"
                  :preview-src-list="[parseImageUrl(message.content)]"
                  fit="cover"
                />
              </template>
              <template v-else-if="message.contentType === contentType.AUDIO">
                <div class="audio-message">
                  <audio controls preload="none" :src="parseAttachment(message.content).url" />
                  <span v-if="parseAttachment(message.content).duration"
                    >{{ parseAttachment(message.content).duration }} 秒</span
                  >
                  <p v-if="parseAttachment(message.content).transcript" class="audio-transcript">
                    {{ parseAttachment(message.content).transcript }}
                  </p>
                  <el-button
                    v-else
                    type="text"
                    size="mini"
                    :loading="Boolean(transcribingMessageIds[message.id])"
                    @click="transcribeAudio(message)"
                    >转文字</el-button
                  >
                </div>
              </template>
              <template v-else-if="message.contentType === contentType.FILE">
                <a
                  class="file-message"
                  :href="parseAttachment(message.content).url"
                  target="_blank"
                  rel="noopener"
                >
                  <i class="el-icon-document" />
                  <span>{{ parseAttachment(message.content).name || "聊天文件" }}</span>
                  <small>{{ formatFileSize(parseAttachment(message.content).size) }}</small>
                </a>
              </template>
              <template v-else-if="message.contentType === contentType.PRODUCT">
                <button
                  type="button"
                  class="rich-card product-message-card"
                  :disabled="!productMessage(message.content).goodsId"
                  @click="jumpToGoods(productMessage(message.content))"
                >
                  <el-image
                    v-if="productMessage(message.content).image"
                    class="product-message-card__image"
                    :src="productMessage(message.content).image"
                    fit="cover"
                  />
                  <span v-else class="product-message-card__placeholder"
                    ><i class="el-icon-goods"
                  /></span>
                  <span class="product-message-card__body">
                    <small>商品 ID：{{ productMessage(message.content).goodsId || "-" }}</small>
                    <strong>{{ productMessage(message.content).name }}</strong>
                    <span class="product-message-card__price"
                      >￥{{ formatMoney(productMessage(message.content).price) }}</span
                    >
                    <span
                      v-if="productMessage(message.content).goodsId"
                      class="product-message-card__action"
                      >查看商品</span
                    >
                  </span>
                </button>
              </template>
              <template v-else-if="message.contentType === contentType.ORDER">
                <button
                  type="button"
                  class="rich-card order-message-card"
                  @click="jumpToOrder(orderMessage(message.content))"
                >
                  <el-image
                    v-if="orderMessage(message.content).image"
                    class="product-message-card__image"
                    :src="orderMessage(message.content).image"
                    fit="cover"
                  />
                  <span v-else class="product-message-card__placeholder"
                    ><i class="el-icon-s-order"
                  /></span>
                  <span class="product-message-card__body">
                    <small>订单编号：{{ orderMessage(message.content).no || "-" }}</small>
                    <strong>{{ orderMessage(message.content).goodsName || "交易订单" }}</strong>
                    <span v-if="orderMessage(message.content).specName">{{
                      orderMessage(message.content).specName
                    }}</span>
                    <span class="product-message-card__price"
                      >￥{{ formatMoney(orderMessage(message.content).amount) }}</span
                    >
                    <span class="product-message-card__action">查看订单</span>
                  </span>
                </button>
              </template>
              <template v-else-if="message.contentType === contentType.QUOTE">
                <div class="quoted-message">
                  <div class="quoted-message__source">
                    <strong>{{
                      (parseJson(message.content).quote &&
                        parseJson(message.content).quote.senderName) ||
                        "聊天消息"
                    }}</strong>
                    <span>{{ quotePreview(parseJson(message.content).quote) }}</span>
                  </div>
                  <div>{{ parseJson(message.content).text }}</div>
                </div>
              </template>
              <template v-else>{{ message.content }}</template>
            </div>
            <div class="message-time">
              {{ formatDate(message.ctdate || message.createTime || message.mddate) }}
            </div>
            <div v-if="message.contentType !== contentType.RECALLED" class="message-actions">
              <el-button type="text" size="mini" @click="startQuoteMessage(message)"
                >引用</el-button
              >
              <el-button
                v-if="canRecallMessage(message)"
                type="text"
                size="mini"
                @click="recallMessage(message)"
                >撤回</el-button
              >
            </div>
          </div>

          <div v-if="!messageLoading && messageList.length === 0" class="empty-box">
            当前会话暂无消息
          </div>
        </template>
        <div v-else class="empty-box">从左侧选择一个会话开始接待</div>
      </main>

      <footer ref="composer" class="composer">
        <div class="tool-row old-tool-row">
          <el-button type="text" icon="el-icon-chat-dot-round" @click="appendEmoji">表情</el-button>
          <el-button type="text" icon="el-icon-picture-outline" @click="imageMode = !imageMode">
            图片
          </el-button>
        </div>
        <div class="tool-row">
          <el-button
            type="text"
            icon="el-icon-chat-dot-round"
            @click="showEmojiPanel = !showEmojiPanel"
            >表情</el-button
          >
          <el-button
            type="text"
            icon="el-icon-picture-outline"
            :disabled="imageUploading"
            @click="$refs.imageInput.click()"
            >图片</el-button
          >
          <input
            ref="imageInput"
            class="image-input"
            type="file"
            accept="image/*"
            @change="handleImageUpload"
          />
          <el-button
            type="text"
            icon="el-icon-document"
            :disabled="attachmentUploading"
            @click="$refs.fileInput.click()"
            >文件</el-button
          >
          <input ref="fileInput" class="image-input" type="file" @change="handleFileUpload" />
          <el-button
            type="text"
            :class="{ 'recording-button': recording }"
            :icon="recording ? 'el-icon-video-pause' : 'el-icon-microphone'"
            :disabled="attachmentUploading"
            @click="toggleVoiceRecording"
          >
            {{ recording ? `停止并发送 ${recordingSeconds}s` : "语音" }}
          </el-button>
        </div>
        <div v-if="showEmojiPanel" ref="emojiPanel" class="emoji-panel" @click.stop>
          <button v-for="emoji in emojiList" :key="emoji" type="button" @click="appendEmoji(emoji)">
            {{ emoji }}
          </button>
        </div>
        <div v-if="quotingMessage" class="quote-composer-preview">
          <div>
            <strong
              >引用
              {{
                quotingMessage.senderName || (quotingMessage.senderType === 1 ? "客服" : "用户")
              }}</strong
            >
            <span>{{ quotePreview(quotingMessage) }}</span>
          </div>
          <el-button
            type="text"
            icon="el-icon-close"
            aria-label="取消引用"
            @click="cancelQuoteMessage"
          />
        </div>
        <el-input
          v-model="messageContent"
          :placeholder="
            imageMode ? '请输入图片 URL，Enter 发送' : '输入消息，Enter 发送，Shift+Enter 换行'
          "
          type="textarea"
          :rows="3"
          resize="none"
          @keydown.native="handleInputKeydown"
          @paste.native="handlePaste"
        />
        <div class="send-row">
          <span>{{ wsConnected ? "实时推送中" : "连接断开，重连中" }}</span>
          <el-button
            type="primary"
            :loading="imageUploading"
            :disabled="!activeConversation || !messageContent.trim() || imageUploading"
            @click="sendMessage"
          >
            发送
          </el-button>
        </div>
      </footer>
    </section>

    <aside class="member-panel">
      <el-tabs v-model="activeTab" stretch>
        <el-tab-pane label="会员信息" name="member">
          <div v-if="activeConversation" v-loading="memberDetailLoading" class="member-card">
            <el-image
              v-if="memberInfo.avatarUrl"
              class="member-avatar member-avatar-image"
              :src="memberInfo.avatarUrl"
              fit="cover"
            />
            <div v-else class="member-avatar">
              {{ getAvatarText(memberInfo.name || activeConversation.userNickname) }}
            </div>
            <h3>{{ memberInfo.name || activeConversation.userNickname || "访客" }}</h3>
            <p>用户ID：{{ memberInfo.id || activeConversation.userId }}</p>
            <p v-if="memberInfo.mobile">手机号：{{ memberInfo.mobile }}</p>
            <p v-if="memberInfo.gradeName">会员等级：{{ memberInfo.gradeName }}</p>
            <p v-if="memberInfo.agentName">代理商：{{ memberInfo.agentName }}</p>
            <p v-if="memberInfo.inviteCode">邀请码：{{ memberInfo.inviteCode }}</p>
            <p v-if="memberInfo.ctdate">注册时间：{{ formatDate(memberInfo.ctdate) }}</p>
            <p>会话ID：{{ activeConversation.id }}</p>
            <p>未读消息：{{ activeConversation.adminUnreadMessageCount || 0 }}</p>
            <p>最后联系：{{ formatDate(activeConversation.lastMessageTime) }}</p>
          </div>
          <div v-else class="empty-box">请选择会话查看会员信息</div>
        </el-tab-pane>

        <el-tab-pane label="最近浏览" name="browse">
          <div v-if="browseProducts.length">
            <div
              v-for="item in browseProducts"
              :key="item.key"
              class="side-product clickable-side-card"
              @click="jumpToGoods(item)"
            >
              <el-image
                v-if="item.image"
                class="side-thumb side-thumb-image"
                :src="item.image"
                fit="cover"
              />
              <div v-else class="side-thumb"></div>
              <div class="side-card-body">
                <strong>{{ item.name }}</strong>
                <p v-if="item.specName || item.specs">{{ item.specName || item.specs }}</p>
                <p>库存: {{ item.stock }} 销量: {{ item.sales }}</p>
                <span>￥{{ formatMoney(item.price) }}</span>
                <el-button type="text" size="mini" @click.stop="sendProductCard(item)"
                  >发送商品</el-button
                >
              </div>
            </div>
          </div>
          <div v-else class="empty-box">暂无最近浏览商品</div>
        </el-tab-pane>

        <el-tab-pane label="交易订单" name="orders">
          <div v-if="tradeOrders.length" v-loading="tradeOrderLoading">
            <div
              v-for="item in tradeOrders"
              :key="item.key"
              class="side-order clickable-side-card"
              @click="jumpToOrder(item)"
            >
              <el-image
                v-if="item.image"
                class="side-thumb side-thumb-image"
                :src="item.image"
                fit="cover"
              />
              <div v-else class="side-thumb"></div>
              <div class="side-card-body">
                <strong>{{ item.goodsName || "订单 " + item.no }}</strong>
                <p v-if="item.specName">{{ item.specName }}</p>
                <p>订单编号: {{ item.no }}</p>
                <p>支付时间: {{ item.payTime ? formatDate(item.payTime) : "-" }}</p>
                <span>￥{{ formatMoney(item.amount) }}</span>
                <el-button type="text" size="mini" @click.stop="sendOrderCard(item)"
                  >发送订单</el-button
                >
              </div>
            </div>
          </div>
          <div v-else v-loading="tradeOrderLoading" class="empty-box">暂无交易订单</div>
        </el-tab-pane>
      </el-tabs>
    </aside>

    <el-dialog
      title="主动联系用户"
      :visible.sync="contactDialogVisible"
      width="520px"
      append-to-body
      @open="searchContactUsers"
    >
      <div class="contact-search">
        <el-input
          v-model.trim="contactKeyword"
          clearable
          placeholder="搜索用户ID、姓名、昵称或手机号"
          @keyup.enter.native="searchContactUsers"
        />
        <el-button type="primary" @click="searchContactUsers">搜索</el-button>
      </div>
      <div v-loading="contactUsersLoading" class="contact-user-list">
        <button
          v-for="user in contactUsers"
          :key="user.id"
          class="contact-user"
          @click="startConversationWithUser(user)"
        >
          <el-image
            v-if="user.avatarUrl"
            class="contact-avatar"
            :src="user.avatarUrl"
            fit="cover"
          />
          <span v-else class="contact-avatar contact-avatar--fallback">{{
            getAvatarText(user.name)
          }}</span>
          <span class="contact-user__main">
            <strong>{{ user.name }}</strong>
            <small
              >用户ID：{{ user.id
              }}<template v-if="user.mobile"> {{ user.mobile }}</template></small
            >
          </span>
          <span class="contact-user__action">发起会话</span>
        </button>
        <div v-if="!contactUsersLoading && !contactUsers.length" class="empty-box">
          没有找到符合条件的用户
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  KEFU_CONTENT_TYPE,
  archiveKefuConversation,
  assignKefuConversation,
  buildKefuWebSocketUrl,
  deleteKefuConversation,
  getKefuBrowseHistory,
  getKefuContactUsers,
  getKefuConversationList,
  getKefuAgentList,
  getKefuMessageList,
  getKefuTradeOrders,
  openKefuConversation,
  takeoverKefuConversation,
  returnKefuConversationToAi,
  recallKefuMessage,
  restoreKefuConversation,
  sendKefuMessage,
  transcribeKefuMessage,
  uploadKefuAttachment,
  uploadKefuImage,
  updateKefuConversationPinned,
  updateKefuMessageReadStatus
} from "@/api/kefu";
import { getUser } from "@/utils/auth";
import { getMemberDetail } from "@/api/member";

export default {
  name: "CustomerService",
  data() {
    return {
      contentType: KEFU_CONTENT_TYPE,
      conversationLoading: false,
      messageLoading: false,
      conversationList: [],
      conversationScope: "PENDING",
      agentList: [],
      messageList: [],
      messagePage: 1,
      messagePageSize: 20,
      messageTotal: 0,
      messageHasMore: false,
      loadingMoreMessages: false,
      activeConversation: null,
      memberDetail: {},
      memberDetailLoading: false,
      browseHistoryList: [],
      browseHistoryLoading: false,
      tradeOrderList: [],
      tradeOrderLoading: false,
      messageContent: "",
      quotingMessage: null,
      imageMode: false,
      imageUploading: false,
      attachmentUploading: false,
      recording: false,
      recordingSeconds: 0,
      recordingStartedAt: 0,
      recordingTimer: null,
      mediaRecorder: null,
      mediaStream: null,
      audioChunks: [],
      showEmojiPanel: false,
      emojiList: [
        "😀",
        "😃",
        "😄",
        "😁",
        "😆",
        "😊",
        "🙂",
        "😉",
        "😍",
        "😘",
        "😎",
        "🤔",
        "😢",
        "😭",
        "😡",
        "👍",
        "👏",
        "🙏",
        "🎉",
        "❤️",
        "🔥",
        "🌹",
        "💯",
        "✅",
        "😂",
        "😅",
        "😋",
        "😴",
        "🤝",
        "👌"
      ],
      activeTab: "member",
      ws: null,
      wsConnected: false,
      reconnectTimer: null,
      reconnectAttempt: 0,
      heartbeatTimer: null,
      networkOnline: true,
      pageAlive: true,
      contactDialogVisible: false,
      contactKeyword: "",
      contactUsers: [],
      contactUsersLoading: false,
      transcribingMessageIds: {}
    };
  },
  computed: {
    currentAdminId() {
      return Number((getUser() || {}).id || 0);
    },
    enabledAgents() {
      return this.agentList.filter(item => item.enabled && item.status === 0);
    },
    serviceAvatar() {
      return this.$store.state.settings.logo;
    },
    memberInfo() {
      return this.memberDetail || {};
    },
    browseProducts() {
      if (this.browseHistoryList.length) {
        return this.browseHistoryList.map((item, index) => ({
          key: item.id || index,
          goodsId: item.goodsId,
          name: item.goodsName || "商品",
          image: item.goodsMainGraph,
          specs: item.specs,
          specName: item.specName,
          stock: item.stock || 0,
          sales: item.sales || 0,
          price: item.currentPrice || item.goodsPrice || "0.00"
        }));
      }
      return this.messageList
        .filter(item => item.contentType === KEFU_CONTENT_TYPE.PRODUCT)
        .map((item, index) => {
          const data = this.parseJson(item.content);
          return {
            key: item.id || index,
            goodsId: data.goodsId || data.id,
            name: data.goodsName || data.spuName || data.name || "商品",
            image: data.mainGraph || data.goodsMainGraph || data.image,
            specs: data.specs,
            specName: data.specName,
            stock: data.stock || 0,
            sales: data.salesCount || data.sales || 0,
            price:
              data.currentPrice ||
              data.goodsPrice ||
              data.adPrice ||
              data.minPrice ||
              data.price ||
              "0.00"
          };
        });
    },
    tradeOrders() {
      if (this.tradeOrderList.length) {
        return this.tradeOrderList.map((item, index) => ({
          key: item.id || index,
          id: item.id,
          no: item.orderNo || item.no || item.id || "-",
          status: this.formatOrderStatus(item.status),
          amount: item.payAmount || item.amount || item.totalAmount || "0.00",
          payTime: item.payTime,
          goodsId: item.goodsId,
          goodsName: item.goodsName,
          image: item.goodsMainGraph,
          specName: item.specName
        }));
      }
      return this.messageList
        .filter(item => item.contentType === KEFU_CONTENT_TYPE.ORDER)
        .map((item, index) => {
          const data = this.parseJson(item.content);
          return {
            key: item.id || index,
            id: data.id,
            no: data.no || data.id || "-",
            status: data.statusName || data.status || "订单消息",
            amount: data.payPrice || data.price || data.amount || "0.00",
            payTime: data.payTime,
            goodsId: data.goodsId,
            goodsName: data.goodsName,
            image: data.mainGraph || data.goodsMainGraph || data.image,
            specName: data.specName
          };
        });
    }
  },
  created() {
    this.loadAgentList();
    this.loadConversationList();
    this.connectWebSocket();
  },
  mounted() {
    document.addEventListener("click", this.closeEmojiPanelOnOutsideClick);
    window.addEventListener("online", this.handleNetworkOnline);
    window.addEventListener("offline", this.handleNetworkOffline);
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
  },
  beforeDestroy() {
    this.pageAlive = false;
    this.cancelVoiceRecording();
    document.removeEventListener("click", this.closeEmojiPanelOnOutsideClick);
    window.removeEventListener("online", this.handleNetworkOnline);
    window.removeEventListener("offline", this.handleNetworkOffline);
    document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    this.stopWebSocketHeartbeat();
    if (this.ws) {
      this.ws.close();
    }
    clearTimeout(this.reconnectTimer);
  },
  methods: {
    async loadAgentList() {
      try {
        this.agentList = await getKefuAgentList({ enabled: true });
      } catch (error) {
        this.agentList = [];
      }
    },
    handleScopeChange() {
      this.activeConversation = null;
      this.messageList = [];
      this.loadConversationList();
    },
    openContactDialog() {
      this.contactDialogVisible = true;
    },
    async searchContactUsers() {
      this.contactUsersLoading = true;
      try {
        this.contactUsers = await getKefuContactUsers({
          keyword: this.contactKeyword,
          page: 1,
          pageSize: 20
        });
      } catch (error) {
        this.contactUsers = [];
        this.$message.error(error.message || "查询用户失败");
      } finally {
        this.contactUsersLoading = false;
      }
    },
    async startConversationWithUser(user) {
      if (!user || !user.id) return;
      try {
        const conversation = await openKefuConversation(user.id);
        this.contactDialogVisible = false;
        this.patchConversation(conversation);
        await this.selectConversation(conversation);
        this.$message.success(`已打开与${user.name || "该用户"}的会话`);
      } catch (error) {
        this.$message.error(error.message || "发起会话失败");
      }
    },
    async transcribeAudio(message) {
      if (!message || !message.id || this.transcribingMessageIds[message.id]) return;
      this.$set(this.transcribingMessageIds, message.id, true);
      try {
        const updated = await transcribeKefuMessage(message.id);
        const index = this.messageList.findIndex(item => item.id === updated.id);
        if (index >= 0)
          this.$set(this.messageList, index, Object.assign({}, this.messageList[index], updated));
      } catch (error) {
        this.$message.error(error.message || "语音识别失败");
      } finally {
        this.$delete(this.transcribingMessageIds, message.id);
      }
    },
    async loadConversationList() {
      this.conversationLoading = true;
      try {
        this.conversationList = await getKefuConversationList({ scope: this.conversationScope });
        if (!this.activeConversation && this.conversationList.length) {
          await this.selectConversation(this.conversationList[0]);
        } else if (this.activeConversation) {
          const latest = this.conversationList.find(item => item.id === this.activeConversation.id);
          if (latest) {
            this.activeConversation = latest;
          }
        }
      } catch (error) {
        this.$message.error(error.message || "加载会话失败");
      } finally {
        this.conversationLoading = false;
      }
    },
    async selectConversation(item) {
      this.activeConversation = item;
      this.memberDetail = {};
      this.browseHistoryList = [];
      this.tradeOrderList = [];
      this.loadMemberDetail(item);
      this.loadBrowseHistory(item);
      this.loadTradeOrders(item);
      try {
        await this.loadMessages(item.id, 1, true);
        await updateKefuMessageReadStatus(item.id);
        item.adminUnreadMessageCount = 0;
      } catch (error) {
        this.$message.error(error.message || "加载消息失败");
      }
    },
    async loadMessages(conversationId, page = 1, isInitial = false) {
      if (isInitial) {
        this.messageLoading = true;
        this.messagePage = 1;
        this.messageList = [];
        this.messageHasMore = false;
        this.messageTotal = 0;
      } else {
        this.loadingMoreMessages = true;
      }

      try {
        const res = await getKefuMessageList(conversationId, {
          page,
          pageSize: this.messagePageSize || 20
        });

        let dataObj = res;
        if (res && res.data && typeof res.data === 'object' && !Array.isArray(res.data)) {
          dataObj = res.data;
        }

        let list = [];
        if (Array.isArray(dataObj)) {
          list = dataObj;
        } else if (dataObj && Array.isArray(dataObj.entitys)) {
          list = dataObj.entitys;
        } else if (dataObj && Array.isArray(dataObj.list)) {
          list = dataObj.list;
        } else if (res && Array.isArray(res.entitys)) {
          list = res.entitys;
        }

        // 接口返回的数据进行倒序处理，使其按时间正序排列
        list = [...list].reverse();

        const pageSize = this.messagePageSize || 20;
        let fetchedTotal =
          (dataObj &&
            (typeof dataObj.totalCount === "number"
              ? dataObj.totalCount
              : typeof dataObj.count === "number"
              ? dataObj.count
              : dataObj.total)) ||
          (res &&
            (typeof res.totalCount === "number"
              ? res.totalCount
              : typeof res.count === "number"
              ? res.count
              : res.total));

        const total = typeof fetchedTotal === "number" ? fetchedTotal : (this.messageTotal || list.length);

        if (isInitial) {
          this.messageList = list;
          this.messagePage = 1;
          this.messageTotal = total;
          this.messageHasMore = list.length >= pageSize && (total > 0 ? list.length < total : true);
          this.scrollToBottom();
        } else {
          const messageBody = this.$refs.messageBody;
          const oldScrollHeight = messageBody ? messageBody.scrollHeight : 0;

          const existingIds = new Set(this.messageList.map(m => m.id));
          const newMessages = list.filter(m => !existingIds.has(m.id));

          // 聊天记录：将更早的历史消息前置追加到顶部
          this.messageList = [...newMessages, ...this.messageList];
          this.messagePage = page;
          this.messageTotal = Math.max(total, this.messageList.length);
          this.messageHasMore = list.length >= pageSize && (total > 0 ? this.messageList.length < total : true);

          // 保持滚动高度位置相对稳定，避免画面跳变
          this.$nextTick(() => {
            if (messageBody) {
              messageBody.scrollTop = messageBody.scrollHeight - oldScrollHeight;
            }
          });
        }
      } finally {
        if (isInitial) {
          this.messageLoading = false;
        } else {
          this.loadingMoreMessages = false;
        }
      }
    },
    async loadMoreMessages() {
      if (this.loadingMoreMessages || !this.messageHasMore || !this.activeConversation) return;
      this.loadingMoreMessages = true;
      try {
        await this.loadMessages(this.activeConversation.id, this.messagePage + 1, false);
      } catch (error) {
        this.$message.error(error.message || "加载历史消息失败");
      } finally {
        this.loadingMoreMessages = false;
      }
    },
    handleMessageScroll(e) {
      if (!e || !e.target) return;
      const { scrollTop } = e.target;
      // 当往上拉动 / 向上滚动到顶部 50px 范围，自动触发加载更早的历史消息
      if (scrollTop < 50 && this.messageHasMore && !this.loadingMoreMessages && !this.messageLoading) {
        this.loadMoreMessages();
      }
    },
    async loadMemberDetail(item) {
      const userId = item && item.userId;
      if (!userId) return;
      this.memberDetailLoading = true;
      try {
        const response = await getMemberDetail({ id: userId });
        this.memberDetail = response.data || {};
      } catch (error) {
        this.memberDetail = {};
        this.$message.warning(error.message || "会员详情加载失败");
      } finally {
        this.memberDetailLoading = false;
      }
    },
    async loadTradeOrders(item) {
      const userId = item && item.userId;
      if (!userId) return;
      this.tradeOrderLoading = true;
      try {
        const response = await getKefuTradeOrders({ page: 1, pageSize: 10, userId });
        this.tradeOrderList = (response.data && response.data.entitys) || [];
      } catch (error) {
        this.tradeOrderList = [];
        this.$message.warning(error.message || "交易订单加载失败");
      } finally {
        this.tradeOrderLoading = false;
      }
    },
    async loadBrowseHistory(item) {
      const userId = item && item.userId;
      if (!userId) return;
      this.browseHistoryLoading = true;
      try {
        const response = await getKefuBrowseHistory({ page: 1, pageSize: 10, userId });
        this.browseHistoryList = (response.data && response.data.entitys) || [];
      } catch (error) {
        this.browseHistoryList = [];
        this.$message.warning(error.message || "最近浏览加载失败");
      } finally {
        this.browseHistoryLoading = false;
      }
    },
    async sendMessage() {
      if (!this.activeConversation || !this.messageContent.trim()) {
        return;
      }
      const text = this.messageContent.trim();
      const quote = this.quotingMessage;
      const contentType = quote
        ? KEFU_CONTENT_TYPE.QUOTE
        : this.imageMode
        ? KEFU_CONTENT_TYPE.IMAGE
        : KEFU_CONTENT_TYPE.TEXT;
      const content = quote
        ? JSON.stringify({
            text,
            quote: {
              messageId: quote.id,
              senderType: quote.senderType,
              senderId: quote.senderId,
              senderName: quote.senderName,
              contentType: quote.contentType,
              content: quote.content
            }
          })
        : text;
      try {
        const message = await sendKefuMessage({
          conversationId: this.activeConversation.id,
          contentType,
          content
        });
        if (!this.wsConnected) {
          this.handleSocketMessage(message);
        }
        this.messageContent = "";
        this.quotingMessage = null;
      } catch (error) {
        this.$message.error(error.message || "发送失败");
      }
    },
    startQuoteMessage(message) {
      if (!message || message.contentType === KEFU_CONTENT_TYPE.RECALLED) return;
      this.quotingMessage = message;
      this.$nextTick(() => {
        const textarea = this.$refs.composer && this.$refs.composer.querySelector("textarea");
        if (textarea) textarea.focus();
      });
    },
    cancelQuoteMessage() {
      this.quotingMessage = null;
    },
    async sendImageContent(content) {
      if (!this.activeConversation || !content) return;
      try {
        const message = await sendKefuMessage({
          conversationId: this.activeConversation.id,
          contentType: KEFU_CONTENT_TYPE.IMAGE,
          content
        });
        if (!this.wsConnected) {
          this.handleSocketMessage(message);
        }
      } catch (error) {
        this.$message.error(error.message || "图片发送失败");
      }
    },
    async uploadAndSendImage(file) {
      if (!this.activeConversation || !file) return;
      this.imageUploading = true;
      try {
        const imageFile = await this.readImageFile(file);
        const content = await uploadKefuImage(imageFile);
        await this.sendImageContent(content);
      } catch (error) {
        const message = error && error.message ? error.message : error;
        this.$message.error(message || "图片上传或发送失败");
      } finally {
        this.imageUploading = false;
      }
    },
    async sendAttachmentContent(contentType, data) {
      if (!this.activeConversation) return;
      const message = await sendKefuMessage({
        conversationId: this.activeConversation.id,
        contentType,
        content: JSON.stringify(data)
      });
      if (!this.wsConnected) this.handleSocketMessage(message);
    },
    async sendProductCard(item) {
      if (!this.activeConversation) {
        this.$message.warning("请先选择会话");
        return;
      }
      try {
        await this.sendAttachmentContent(KEFU_CONTENT_TYPE.PRODUCT, {
          goodsId: item.goodsId,
          goodsName: item.name,
          name: item.name,
          mainGraph: item.image,
          goodsMainGraph: item.image,
          currentPrice: item.price,
          price: item.price,
          specName: item.specName || item.specs,
          stock: item.stock,
          sales: item.sales
        });
        this.$message.success("商品卡片已发送");
      } catch (error) {
        this.$message.error((error && error.message) || "商品卡片发送失败");
      }
    },
    async sendOrderCard(item) {
      if (!this.activeConversation) {
        this.$message.warning("请先选择会话");
        return;
      }
      try {
        await this.sendAttachmentContent(KEFU_CONTENT_TYPE.ORDER, {
          id: item.id,
          orderId: item.id,
          no: item.no,
          orderNo: item.no,
          goodsId: item.goodsId,
          goodsName: item.goodsName,
          mainGraph: item.image,
          goodsMainGraph: item.image,
          specName: item.specName,
          payTime: item.payTime,
          payPrice: item.amount,
          amount: item.amount,
          status: item.status
        });
        this.$message.success("订单卡片已发送");
      } catch (error) {
        this.$message.error((error && error.message) || "订单卡片发送失败");
      }
    },
    canRecallMessage(message) {
      if (
        !message ||
        message.senderType !== 1 ||
        message.contentType === KEFU_CONTENT_TYPE.RECALLED
      )
        return false;
      let createdAt = Number(message.ctdate || message.createTime || message.mddate || 0);
      if (createdAt > 0 && createdAt < 100000000000) createdAt *= 1000;
      return createdAt > 0 && Date.now() - createdAt <= 3 * 60 * 1000;
    },
    async recallMessage(message) {
      try {
        await this.$confirm("确定撤回这条消息？", "撤回消息", {
          confirmButtonText: "撤回",
          cancelButtonText: "取消",
          type: "warning"
        });
        const recalled = await recallKefuMessage(message.id);
        this.handleSocketRecall(
          recalled || {
            id: message.id,
            contentType: KEFU_CONTENT_TYPE.RECALLED,
            content: "客服撤回了一条消息"
          }
        );
        this.$message.success("消息已撤回");
      } catch (error) {
        if (error === "cancel" || error === "close") return;
        this.$message.error((error && error.message) || "撤回失败，请稍后重试");
      }
    },
    async handleFileUpload(event) {
      const file = event.target.files && event.target.files[0];
      event.target.value = "";
      if (!file || !this.activeConversation) return;
      if (file.size > 50 * 1024 * 1024) {
        this.$message.warning("文件不能超过 50MB");
        return;
      }
      this.attachmentUploading = true;
      try {
        const url = await uploadKefuAttachment(file, "file");
        await this.sendAttachmentContent(KEFU_CONTENT_TYPE.FILE, {
          url,
          name: file.name,
          size: file.size
        });
      } catch (error) {
        this.$message.error((error && error.message) || "文件上传或发送失败");
      } finally {
        this.attachmentUploading = false;
      }
    },
    async toggleVoiceRecording() {
      if (this.recording) {
        this.stopVoiceRecording();
        return;
      }
      if (!this.activeConversation) {
        this.$message.warning("请先选择会话");
        return;
      }
      if (!navigator.mediaDevices || typeof MediaRecorder === "undefined") {
        this.$message.warning("当前浏览器不支持录音");
        return;
      }
      try {
        this.mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const preferred = ["audio/mp4", "audio/webm;codecs=opus", "audio/webm"].find(type =>
          MediaRecorder.isTypeSupported(type)
        );
        this.audioChunks = [];
        this.mediaRecorder = new MediaRecorder(
          this.mediaStream,
          preferred ? { mimeType: preferred } : undefined
        );
        this.mediaRecorder.ondataavailable = event => {
          if (event.data && event.data.size) this.audioChunks.push(event.data);
        };
        this.mediaRecorder.onstop = () => this.finishAdminVoiceRecording();
        this.mediaRecorder.start(500);
        this.recording = true;
        this.recordingSeconds = 0;
        this.recordingStartedAt = Date.now();
        this.recordingTimer = setInterval(() => {
          this.recordingSeconds = Math.min(
            60,
            Math.floor((Date.now() - this.recordingStartedAt) / 1000)
          );
          if (this.recordingSeconds >= 60) this.stopVoiceRecording();
        }, 500);
      } catch (error) {
        this.releaseAdminRecorder();
        this.$message.error("无法使用麦克风，请检查浏览器权限");
      }
    },
    stopVoiceRecording() {
      if (!this.recording) return;
      this.recording = false;
      clearInterval(this.recordingTimer);
      this.recordingTimer = null;
      if (this.mediaRecorder && this.mediaRecorder.state !== "inactive") this.mediaRecorder.stop();
    },
    async finishAdminVoiceRecording() {
      const duration = Math.max(1, Math.round((Date.now() - this.recordingStartedAt) / 1000));
      const mimeType = (this.mediaRecorder && this.mediaRecorder.mimeType) || "audio/webm";
      const extension = mimeType.includes("mp4") ? "m4a" : "webm";
      const file = new File(
        [new Blob(this.audioChunks, { type: mimeType })],
        `voice-${Date.now()}.${extension}`,
        { type: mimeType }
      );
      this.releaseAdminRecorder();
      if (!file.size) return;
      this.attachmentUploading = true;
      try {
        const url = await uploadKefuAttachment(file, "audio");
        await this.sendAttachmentContent(KEFU_CONTENT_TYPE.AUDIO, {
          url,
          duration,
          name: file.name
        });
      } catch (error) {
        this.$message.error((error && error.message) || "语音上传或发送失败");
      } finally {
        this.attachmentUploading = false;
      }
    },
    releaseAdminRecorder() {
      if (this.mediaStream) this.mediaStream.getTracks().forEach(track => track.stop());
      this.mediaStream = null;
      this.mediaRecorder = null;
      this.audioChunks = [];
      this.recording = false;
      clearInterval(this.recordingTimer);
      this.recordingTimer = null;
    },
    cancelVoiceRecording() {
      if (this.mediaRecorder) this.mediaRecorder.onstop = null;
      if (this.mediaRecorder && this.mediaRecorder.state !== "inactive") this.mediaRecorder.stop();
      this.releaseAdminRecorder();
    },
    parseAttachment(content) {
      try {
        const data = typeof content === "string" ? JSON.parse(content) : content;
        return data && typeof data === "object" ? data : { url: String(data || "") };
      } catch (error) {
        return { url: String(content || "") };
      }
    },
    formatFileSize(size) {
      const value = Number(size || 0);
      if (!value) return "点击查看";
      if (value < 1024 * 1024) return `${Math.max(1, Math.round(value / 1024))} KB`;
      return `${(value / 1024 / 1024).toFixed(1)} MB`;
    },
    readImageFile(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = event => {
          const img = new Image();
          img.onload = () => {
            const render = (maxSide, quality) => {
              const ratio = Math.min(1, maxSide / Math.max(img.width, img.height));
              const canvas = document.createElement("canvas");
              canvas.width = Math.max(1, Math.round(img.width * ratio));
              canvas.height = Math.max(1, Math.round(img.height * ratio));
              const ctx = canvas.getContext("2d");
              ctx.fillStyle = "#fff";
              ctx.fillRect(0, 0, canvas.width, canvas.height);
              ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
              return canvas;
            };
            let maxSide = 1024;
            let quality = 0.72;
            const canvas = render(maxSide, quality);
            canvas.toBlob(
              blob => {
                if (!blob) {
                  reject(new Error("图片处理失败"));
                  return;
                }
                resolve(new File([blob], `${Date.now()}.jpg`, { type: "image/jpeg" }));
              },
              "image/jpeg",
              quality
            );
          };
          img.onerror = reject;
          img.src = event.target.result;
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },
    async handleImageUpload(event) {
      const file = event.target.files && event.target.files[0];
      event.target.value = "";
      if (!file) return;
      if (!file.type || !file.type.startsWith("image/")) {
        this.$message.warning("请选择图片文件");
        return;
      }
      await this.uploadAndSendImage(file);
    },
    async handlePaste(event) {
      const items = event.clipboardData && event.clipboardData.items;
      if (!items) return;
      for (let i = 0; i < items.length; i += 1) {
        const item = items[i];
        if (item.type && item.type.startsWith("image/")) {
          event.preventDefault();
          const file = item.getAsFile();
          await this.uploadAndSendImage(file);
          return;
        }
      }
    },
    async togglePinned() {
      if (!this.activeConversation) return;
      await updateKefuConversationPinned({
        id: this.activeConversation.id,
        adminPinned: !this.activeConversation.adminPinned
      });
      this.$message.success("操作成功");
      this.loadConversationList();
    },
    async removeConversation() {
      if (!this.activeConversation) return;
      await this.$confirm("确认删除该会话？", "提示", { type: "warning" });
      await deleteKefuConversation(this.activeConversation.id);
      this.activeConversation = null;
      this.messageList = [];
      this.loadConversationList();
    },
    serviceModeLabel(mode) {
      return (
        {
          AI_ACTIVE: "AI 接待中",
          HUMAN_PENDING: "等待人工",
          HUMAN_ACTIVE: "人工接待中",
          CLOSED: "已结束"
        }[mode || "AI_ACTIVE"] || "AI 接待中"
      );
    },
    serviceModeShort(mode) {
      return (
        { AI_ACTIVE: "AI", HUMAN_PENDING: "待接", HUMAN_ACTIVE: "人工", CLOSED: "结束" }[
          mode || "AI_ACTIVE"
        ] || "AI"
      );
    },
    serviceModeTagType(mode) {
      return { AI_ACTIVE: "info", HUMAN_PENDING: "warning", HUMAN_ACTIVE: "success", CLOSED: "" }[
        mode || "AI_ACTIVE"
      ];
    },
    async takeoverConversation() {
      if (!this.activeConversation) return;
      this.activeConversation = await takeoverKefuConversation(this.activeConversation.id);
      this.conversationScope = "MINE";
      await this.loadConversationList();
      await this.loadAgentList();
      this.$message.success("已接入该用户的人工会话");
    },
    async assignConversation(adminUserId) {
      if (!this.activeConversation) return;
      try {
        this.activeConversation = await assignKefuConversation(
          this.activeConversation.id,
          adminUserId
        );
        this.patchConversation(this.activeConversation);
        await this.loadAgentList();
        this.$message.success("会话已指派");
      } catch (error) {
        this.$message.error(error.message || "会话指派失败");
      }
    },
    async archiveConversation() {
      if (!this.activeConversation) return;
      try {
        await this.$confirm("归档后会话将移入已归档列表，聊天记录仍会完整保留。", "归档会话", {
          confirmButtonText: "归档",
          cancelButtonText: "取消",
          type: "warning"
        });
        await archiveKefuConversation(this.activeConversation.id);
        this.activeConversation = null;
        this.messageList = [];
        await this.loadConversationList();
        this.$message.success("会话已归档");
      } catch (error) {
        if (error === "cancel" || error === "close") return;
        this.$message.error(error.message || "归档失败");
      }
    },
    async restoreConversation() {
      if (!this.activeConversation) return;
      try {
        await restoreKefuConversation(this.activeConversation.id);
        this.activeConversation = null;
        this.messageList = [];
        await this.loadConversationList();
        this.$message.success("会话已恢复到待分配列表");
      } catch (error) {
        this.$message.error(error.message || "恢复会话失败");
      }
    },
    async returnConversationToAi() {
      if (!this.activeConversation) return;
      this.activeConversation = await returnKefuConversationToAi(this.activeConversation.id);
      this.patchConversation(this.activeConversation);
      this.$message.success("会话已交回 AI 智能客服");
    },
    connectWebSocket() {
      if (!this.pageAlive || !this.networkOnline) return;
      if (
        this.ws &&
        (this.ws.readyState === WebSocket.CONNECTING || this.ws.readyState === WebSocket.OPEN)
      )
        return;
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;

      const socket = new WebSocket(buildKefuWebSocketUrl());
      this.ws = socket;
      socket.onopen = () => {
        if (this.ws !== socket) return;
        this.wsConnected = true;
        this.reconnectAttempt = 0;
        this.startWebSocketHeartbeat(socket);
      };
      socket.onclose = () => {
        if (this.ws !== socket) return;
        this.stopWebSocketHeartbeat();
        this.ws = null;
        this.wsConnected = false;
        this.scheduleWebSocketReconnect();
      };
      socket.onerror = () => {
        if (this.ws !== socket) return;
        this.wsConnected = false;
        try {
          socket.close();
        } catch (error) {
          this.scheduleWebSocketReconnect();
        }
      };
      socket.onmessage = event => {
        if (this.ws !== socket) return;
        let data;
        try {
          data = JSON.parse(event.data);
        } catch (error) {
          return;
        }
        if (data.type === "pong") return;
        if (data.type === "kefu_message_type") {
          this.handleSocketMessage(data.content);
        }
        if (data.type === "kefu_conversation_change") {
          this.handleSocketConversation(data.content);
        }
        if (data.type === "kefu_transfer_requested") {
          this.handleSocketConversation(data.content);
          this.$message.warning(
            `${(data.content && data.content.userNickname) || "用户"} 正在等待人工客服`
          );
        }
        if (data.type === "kefu_service_state") {
          this.handleSocketConversation(data.content);
        }
        if (data.type === "kefu_message_read_status_change") {
          this.handleSocketReadStatus(data.content);
        }
        if (data.type === "kefu_message_recall") {
          this.handleSocketRecall(data.content);
        }
      };
    },
    scheduleWebSocketReconnect() {
      if (!this.pageAlive || !this.networkOnline || this.reconnectTimer) return;
      const delays = [2000, 4000, 8000, 15000, 30000];
      const delay = delays[Math.min(this.reconnectAttempt, delays.length - 1)];
      this.reconnectAttempt += 1;
      this.reconnectTimer = setTimeout(() => {
        this.reconnectTimer = null;
        this.connectWebSocket();
      }, delay);
    },
    startWebSocketHeartbeat(socket) {
      this.stopWebSocketHeartbeat();
      this.heartbeatTimer = setInterval(() => {
        if (this.ws !== socket || socket.readyState !== WebSocket.OPEN) return;
        try {
          socket.send("ping");
        } catch (error) {
          socket.close();
        }
      }, 25000);
    },
    stopWebSocketHeartbeat() {
      if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    },
    reconnectWebSocketNow() {
      if (!this.pageAlive || !this.networkOnline || this.wsConnected) return;
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
      this.connectWebSocket();
    },
    handleNetworkOnline() {
      this.networkOnline = true;
      this.reconnectWebSocketNow();
    },
    handleNetworkOffline() {
      this.networkOnline = false;
      this.wsConnected = false;
      this.stopWebSocketHeartbeat();
      const socket = this.ws;
      if (socket && socket.readyState !== WebSocket.CLOSED) {
        try {
          socket.close(1000, "network offline");
        } catch (error) {
          // The browser may already have disposed the socket.
        }
      }
    },
    handleVisibilityChange() {
      if (document.visibilityState === "visible") this.reconnectWebSocketNow();
    },
    handleSocketConversation(conversation) {
      if (!conversation) return;
      this.patchConversation(conversation);
      if (
        this.activeConversation &&
        conversation &&
        conversation.id === this.activeConversation.id
      ) {
        this.activeConversation = Object.assign({}, this.activeConversation, conversation);
      }
    },
    handleSocketMessage(message) {
      if (!message) return;
      this.patchConversationByMessage(message);
      if (this.activeConversation && message.conversationId === this.activeConversation.id) {
        const exists = this.messageList.some(item => item.id === message.id);
        if (!exists) {
          this.messageList.push(message);
          this.scrollToBottom();
        }
        updateKefuMessageReadStatus(this.activeConversation.id);
      }
    },
    handleSocketRecall(message) {
      if (!message) return;
      const index = this.messageList.findIndex(item => item.id === message.id);
      if (index >= 0) {
        this.$set(this.messageList, index, Object.assign({}, this.messageList[index], message));
      }
      this.patchConversationByMessage(message);
    },
    patchConversation(conversation) {
      const index = this.conversationList.findIndex(item => item.id === conversation.id);
      if (!this.conversationMatchesScope(conversation)) {
        if (index > -1) this.conversationList.splice(index, 1);
        return;
      }
      if (index > -1) {
        const next = Object.assign({}, this.conversationList[index], conversation);
        this.conversationList.splice(index, 1);
        this.conversationList.unshift(next);
        if (this.activeConversation && this.activeConversation.id === next.id) {
          this.activeConversation = Object.assign({}, this.activeConversation, next);
        }
      } else {
        this.conversationList.unshift(conversation);
      }
    },
    conversationMatchesScope(conversation) {
      if (!conversation || conversation.isDelete) return false;
      if (this.conversationScope === "ARCHIVED") return Boolean(conversation.archived);
      if (conversation.archived) return false;
      if (this.conversationScope === "MINE")
        return Number(conversation.assignedAdminId || 0) === this.currentAdminId;
      if (this.conversationScope === "PENDING") {
        return (
          conversation.serviceMode === "HUMAN_PENDING" && !Number(conversation.assignedAdminId || 0)
        );
      }
      return true;
    },
    patchConversationByMessage(message) {
      const conversationId = message.conversationId;
      const index = this.conversationList.findIndex(item => item.id === conversationId);
      if (index === -1) return;
      const oldItem = this.conversationList[index];
      const isActive = this.activeConversation && this.activeConversation.id === conversationId;
      const next = Object.assign({}, oldItem, {
        lastMessageContent: message.content,
        lastMessageContentType: message.contentType,
        lastMessageTime: message.ctdate || message.createTime || message.mddate,
        adminUnreadMessageCount: isActive ? 0 : (oldItem.adminUnreadMessageCount || 0) + 1
      });
      this.conversationList.splice(index, 1);
      this.conversationList.unshift(next);
      if (isActive) {
        this.activeConversation = Object.assign({}, this.activeConversation, next);
      }
    },
    handleSocketReadStatus(payload) {
      if (!payload || !payload.conversationId) return;
      const index = this.conversationList.findIndex(item => item.id === payload.conversationId);
      if (index === -1) return;
      const next = Object.assign({}, this.conversationList[index], {
        adminUnreadMessageCount: 0
      });
      this.conversationList.splice(index, 1, next);
      if (this.activeConversation && this.activeConversation.id === payload.conversationId) {
        this.activeConversation = Object.assign({}, this.activeConversation, next);
      }
    },
    handleInputKeydown(event) {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        this.sendMessage();
      }
    },
    closeEmojiPanelOnOutsideClick(event) {
      if (!this.showEmojiPanel) return;
      const panel = this.$refs.emojiPanel;
      if (panel && panel.contains(event.target)) return;
      const clickedButton = event.target.closest && event.target.closest(".el-button");
      const emojiButton =
        (event.target.closest && event.target.closest(".el-icon-chat-dot-round")) ||
        (clickedButton && clickedButton.querySelector(".el-icon-chat-dot-round"));
      if (emojiButton) return;
      this.showEmojiPanel = false;
    },
    appendEmoji(emoji = "😊") {
      this.messageContent += emoji;
      this.showEmojiPanel = false;
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.messageBody;
        if (el) {
          el.scrollTop = el.scrollHeight;
        }
      });
    },
    parseJson(content) {
      try {
        return JSON.parse(content || "{}");
      } catch (error) {
        return {};
      }
    },
    productMessage(content) {
      const data = this.parseJson(content);
      return {
        goodsId: data.goodsId || data.spuId || data.id,
        name: data.goodsName || data.spuName || data.name || "商品信息",
        image: data.goodsMainGraph || data.mainGraph || data.image || data.picUrl,
        price:
          data.currentPrice || data.goodsPrice || data.adPrice || data.minPrice || data.price || 0
      };
    },
    orderMessage(content) {
      const data = this.parseJson(content);
      return {
        id: data.orderId || data.id,
        no: data.orderNo || data.no || data.id,
        goodsName: data.goodsName || data.title,
        image: data.goodsMainGraph || data.mainGraph || data.image || data.picUrl,
        specName: data.specName || data.goodsSpecs,
        amount: data.payAmount || data.payPrice || data.amount || data.price || 0
      };
    },
    parseImageUrl(content) {
      const data = this.parseJson(content);
      return data.picUrl || data.url || content;
    },
    quotePreview(quote) {
      if (!quote) return "";
      if (quote.contentType === KEFU_CONTENT_TYPE.IMAGE) return "[图片]";
      if (quote.contentType === KEFU_CONTENT_TYPE.FILE) return "[文件]";
      if (quote.contentType === KEFU_CONTENT_TYPE.AUDIO) return "[语音]";
      if (quote.contentType === KEFU_CONTENT_TYPE.PRODUCT) return "[商品]";
      if (quote.contentType === KEFU_CONTENT_TYPE.ORDER) return "[订单]";
      return String(quote.content || "").slice(0, 80);
    },
    formatLastMessage(item) {
      if (item.lastMessageContentType === KEFU_CONTENT_TYPE.FILE) return "[文件]";
      if (item.lastMessageContentType === KEFU_CONTENT_TYPE.AUDIO) return "[语音]";
      if (item.lastMessageContentType === KEFU_CONTENT_TYPE.IMAGE) return "[图片]";
      if (item.lastMessageContentType === KEFU_CONTENT_TYPE.PRODUCT) return "[商品]";
      if (item.lastMessageContentType === KEFU_CONTENT_TYPE.ORDER) return "[订单]";
      if (item.lastMessageContentType === KEFU_CONTENT_TYPE.QUOTE) return "[引用消息]";
      if (item.lastMessageContentType === KEFU_CONTENT_TYPE.RECALLED) return "用户撤回了一条消息";
      return item.lastMessageContent || "暂无消息";
    },
    getAvatarText(name) {
      return (name || "访客").slice(0, 1).toUpperCase();
    },
    formatDate(value) {
      if (!value) return "-";
      return new Date(value).toLocaleString();
    },
    formatMoney(value) {
      const number = Number(value || 0);
      if (Number.isNaN(number)) return value || "0.00";
      return number.toFixed(2);
    },
    formatShortDate(value) {
      if (!value) return "";
      const date = new Date(value);
      const month = `${date.getMonth() + 1}`.padStart(2, "0");
      const day = `${date.getDate()}`.padStart(2, "0");
      const hour = `${date.getHours()}`.padStart(2, "0");
      const minute = `${date.getMinutes()}`.padStart(2, "0");
      const second = `${date.getSeconds()}`.padStart(2, "0");
      return `${date.getFullYear()}-${month}-${day} ${hour}:${minute}:${second}`;
    },
    jumpToGoods(item) {
      if (!item || !item.goodsId) return;
      sessionStorage.setItem(
        "editObj",
        JSON.stringify({
          page: 1,
          pageSize: 20,
          keyword: item.name || item.goodsId,
          type: null,
          status: null,
          merchantId: null,
          shopId: null,
          brandId: null,
          categoryId: null,
          deleted: null,
          createStartTime: null,
          createEndTime: null
        })
      );
      this.$router.push({ path: "/product/list", query: { isEdit: 1 } });
    },
    jumpToOrder(item) {
      if (!item || !item.no || item.no === "-") return;
      sessionStorage.setItem(
        "pageState",
        JSON.stringify({
          page: 1,
          pageSize: 20,
          orderNo: item.no
        })
      );
      this.$router.push({ path: "/order/list" });
    },
    formatOrderStatus(status) {
      const statusMap = {
        0: "待支付",
        1: "支付中",
        2: "待发货",
        3: "待收货",
        4: "交易成功",
        5: "交易关闭"
      };
      return statusMap[status] || "订单";
    }
  }
};
</script>

<style scoped lang="scss">
.kefu-page {
  height: calc(100vh - 106px);
  min-height: 0;
  width: 100%;
  max-width: 100%;
  display: grid;
  grid-template-columns: 320px minmax(420px, 1fr) 300px;
  grid-template-rows: minmax(0, 1fr);
  overflow: hidden;
  background: #f4f6f9;
  border: 1px solid #e8edf3;
}

.conversation-panel,
.chat-panel,
.member-panel {
  min-width: 0;
  min-height: 0;
  background: #fff;
}

.conversation-panel {
  border-right: 1px solid #e8edf3;
}

.panel-title,
.chat-header {
  height: 58px;
  padding: 0 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #e8edf3;
}

.panel-actions {
  display: flex;
  align-items: center;
}

.conversation-scopes {
  height: 46px;
  padding: 8px 10px;
  box-sizing: border-box;
  overflow-x: auto;
  white-space: nowrap;
  border-bottom: 1px solid #e8edf3;
}

.conversation-list {
  height: calc(100% - 104px);
  padding-right: 12px;
  box-sizing: border-box;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-gutter: stable;
}

.assigned-agent {
  margin-top: 5px;
  color: #909399;
  font-size: 12px;
}

.conversation-item {
  width: 100%;
  min-height: 84px;
  display: flex;
  gap: 12px;
  padding: 14px 12px 14px 16px;
  border: 0;
  border-bottom: 1px solid #eef2f6;
  background: #fff;
  text-align: left;
  cursor: pointer;
  outline: none;
}

.conversation-item:hover,
.conversation-item.active {
  background: #edf4ff;
}

.avatar,
.message-avatar,
.member-avatar {
  flex: none;
  display: grid;
  place-items: center;
  color: #fff;
  background: #5b8def;
  font-weight: 700;
  border-radius: 50%;
}

.avatar {
  width: 44px;
  height: 44px;
}

.avatar-image,
.message-avatar-image {
  display: block;
}

.conversation-main {
  min-width: 0;
  flex: 1;
  overflow: hidden;
}

.conversation-row {
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  line-height: 24px;
}

.conversation-row strong {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.conversation-row span {
  flex: none;
  max-width: 76px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #8a94a6;
  font-size: 13px;
}

.conversation-date {
  flex: none;
  max-width: none;
  margin-left: 8px;
  text-align: right;
  color: #8a94a6;
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.service-mode-dot {
  flex: none;
  padding: 1px 6px;
  border-radius: 9px;
  background: #eef1f6;
  color: #667085;
  font-size: 11px;
  font-style: normal;
  line-height: 16px;
}

.service-mode-dot--human_pending {
  background: #fff3d6;
  color: #b45309;
}

.service-mode-dot--human_active {
  background: #dcfce7;
  color: #15803d;
}

.service-mode-dot--closed {
  background: #f3f4f6;
  color: #9ca3af;
}

.last-message span {
  flex: 1;
  min-width: 0;
  max-width: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.last-message .service-mode-dot {
  max-width: none;
  color: #667085;
}

.last-message .service-mode-dot--human_pending {
  color: #b45309;
}

.last-message .service-mode-dot--human_active {
  color: #15803d;
}

.last-message .service-mode-dot--closed {
  color: #9ca3af;
}

.last-message em {
  flex: none;
  min-width: 20px;
  max-width: 44px;
  height: 20px;
  padding: 0 6px;
  border-radius: 999px;
  color: #fff;
  background: #f56c6c;
  font-style: normal;
  font-size: 12px;
  line-height: 20px;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chat-panel {
  min-height: 0;
  display: grid;
  grid-template-rows: 58px minmax(0, 1fr) 168px;
  overflow: hidden;
}

.chat-header span {
  display: block;
  margin-top: 4px;
  color: #8a94a6;
  font-size: 12px;
}

.header-actions {
  display: flex;
  gap: 8px;
}

.message-body {
  min-height: 0;
  padding: 20px;
  overflow-y: auto;
  overflow-x: hidden;
  background: #f8fafc;
}

.load-more-history,
.no-more-history {
  text-align: center;
  padding: 4px 0 12px;
  font-size: 12px;
  color: #909399;
}

.load-more-history .el-button {
  font-size: 12px;
  color: #409eff;
}

.message-row {
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
  align-items: flex-start;
}

.message-row.mine {
  flex-direction: row-reverse;
}

.message-avatar {
  width: 38px;
  height: 38px;
  background: #c4cad4;
}

.message-row.mine .message-avatar {
  background: #5b8def;
}

.message-bubble {
  max-width: 68%;
  padding: 10px 12px;
  color: #222;
  background: #fff;
  border: 1px solid #e8edf3;
  border-radius: 8px;
  line-height: 1.6;
  word-break: break-word;
}

.message-row.mine .message-bubble {
  color: #1f3b66;
  background: #dce9ff;
  border-color: #d2e2ff;
}

.recalled-message {
  color: #8a94a6;
  font-size: 12px;
}

.quoted-message {
  min-width: 220px;
}

.quoted-message__source {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 8px;
  padding: 8px 10px;
  border-radius: 6px;
  background: rgba(70, 90, 120, 0.08);
  color: #667085;
  font-size: 12px;
}

.quoted-message__source span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.message-time {
  align-self: flex-end;
  margin: 0 0 2px -2px;
  color: #8a94a6;
  font-size: 12px;
  white-space: nowrap;
}

.message-row.mine .message-time {
  margin: 0 -2px 2px 0;
}

.message-actions {
  align-self: flex-end;
  margin-bottom: 1px;
  display: flex;
  gap: 8px;
}

.message-actions .el-button {
  padding: 4px 2px;
  color: #8a94a6;
}

.message-actions .el-button + .el-button {
  margin-left: 0;
}

.quote-composer-preview {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 12px;
  margin-bottom: 8px;
  border-left: 3px solid #7c3aed;
  border-radius: 4px;
  color: #606266;
  background: #f5f3ff;
}

.quote-composer-preview > div {
  min-width: 0;
}

.quote-composer-preview strong,
.quote-composer-preview span {
  display: block;
}

.quote-composer-preview span {
  margin-top: 3px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.message-image {
  max-width: 220px;
  max-height: 260px;
  border-radius: 6px;
}

.audio-message {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.audio-message audio {
  width: 230px;
  height: 36px;
}

.audio-message span {
  color: #687386;
  font-size: 12px;
  white-space: nowrap;
}

.audio-message .audio-transcript {
  width: 100%;
  margin: 4px 0 0;
  padding-top: 8px;
  border-top: 1px solid #dfe5ed;
  color: #394456;
  font-size: 13px;
  line-height: 1.6;
  white-space: normal;
}

.contact-search {
  display: flex;
  gap: 10px;
}

.contact-user-list {
  max-height: 430px;
  min-height: 160px;
  margin-top: 16px;
  overflow-y: auto;
}

.contact-user {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 8px;
  border: 0;
  border-bottom: 1px solid #edf0f4;
  background: #ffffff;
  color: #283446;
  text-align: left;
  cursor: pointer;
}

.contact-user:hover,
.contact-user:focus-visible {
  background: #f2f6fc;
  outline: none;
}

.contact-avatar {
  width: 42px;
  height: 42px;
  flex: none;
  border-radius: 50%;
}

.contact-avatar--fallback {
  display: grid;
  place-items: center;
  background: #5b8def;
  color: #ffffff;
  font-weight: 700;
}

.contact-user__main {
  min-width: 0;
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 5px;
}

.contact-user__main small {
  color: #667085;
}

.contact-user__action {
  flex: none;
  color: #326dcc;
  font-size: 13px;
}

.file-message {
  display: flex;
  min-width: 230px;
  align-items: center;
  gap: 10px;
  color: #294f86;
  text-decoration: none;
}

.file-message i {
  color: #5b8def;
  font-size: 28px;
}

.file-message span {
  min-width: 0;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-message small {
  color: #8a94a6;
  white-space: nowrap;
}

.recording-button {
  color: #c93636 !important;
}

.rich-card {
  width: 260px;
  display: flex;
  gap: 10px;
  align-items: center;
}

.product-message-card,
.order-message-card {
  padding: 0;
  border: 0;
  color: #1f2937;
  font: inherit;
  text-align: left;
  background: transparent;
  cursor: pointer;
  align-items: stretch;
}

.product-message-card:disabled {
  cursor: default;
}

.product-message-card__image,
.product-message-card__placeholder {
  flex: none;
  width: 76px;
  height: 76px;
  border-radius: 8px;
  overflow: hidden;
}

.product-message-card__placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #7c8799;
  font-size: 24px;
  background: #edf1f6;
}

.product-message-card__body {
  min-width: 0;
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: flex-start;
}

.product-message-card__body small {
  margin-bottom: 3px;
  color: #667085;
  font-size: 11px;
}

.product-message-card__body strong {
  display: -webkit-box;
  max-width: 170px;
  overflow: hidden;
  color: #1f2937;
  font-size: 14px;
  line-height: 1.35;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.product-message-card__price {
  margin-top: auto;
  color: #7a3ff2;
  font-size: 15px;
  font-weight: 600;
}

.product-message-card__action {
  align-self: flex-end;
  margin-top: -20px;
  color: #4777d9;
  font-size: 12px;
}

.thumb,
.side-thumb {
  flex: none;
  width: 54px;
  height: 54px;
  border-radius: 6px;
  background: #d8dee8;
}

.order-thumb {
  background: #cfe1ff;
}

.composer {
  position: relative;
  padding: 10px 14px;
  border-top: 1px solid #e8edf3;
  background: #fff;
  overflow: visible;
}

.tool-row {
  height: 28px;
  display: flex;
  gap: 12px;
  align-items: center;
}

.old-tool-row {
  display: none;
}

.image-input {
  display: none;
}

.emoji-panel {
  position: absolute;
  left: 14px;
  bottom: calc(100% - 6px);
  z-index: 20;
  display: grid;
  grid-template-columns: repeat(10, 30px);
  gap: 6px;
  padding: 10px;
  background: #fff;
  border: 1px solid #dbe3ee;
  border-radius: 8px;
  box-shadow: 0 8px 20px rgba(31, 59, 102, 0.12);
}

.emoji-panel::after {
  content: "";
  position: absolute;
  left: 28px;
  bottom: -7px;
  width: 12px;
  height: 12px;
  background: #fff;
  border-right: 1px solid #dbe3ee;
  border-bottom: 1px solid #dbe3ee;
  transform: rotate(45deg);
}

.emoji-panel button {
  width: 30px;
  height: 30px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  font-size: 18px;
  line-height: 28px;
}

.emoji-panel button:hover {
  background: #eef4ff;
  border-color: #93c5fd;
}

.send-row {
  margin-top: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #8a94a6;
  font-size: 12px;
}

.member-panel {
  border-left: 1px solid #e8edf3;
  padding: 18px 14px;
  overflow: auto;
}

.member-card {
  text-align: center;
}

.member-avatar {
  width: 72px;
  height: 72px;
  margin: 8px auto 12px;
  font-size: 24px;
}

.member-avatar-image {
  display: block;
}

.member-card p {
  margin: 8px 0;
  color: #606b7b;
}

.side-product,
.side-order {
  display: flex;
  gap: 12px;
  padding: 12px;
  margin-bottom: 12px;
  background: #f4f6f9;
  border-radius: 8px;
  border: 1px solid transparent;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.clickable-side-card {
  cursor: pointer;
}

.clickable-side-card:hover {
  background: #eef4ff;
  border-color: #9ec5ff;
}

.side-card-body {
  min-width: 0;
  flex: 1;
}

.side-card-body strong {
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.side-thumb-image {
  display: block;
  overflow: hidden;
}

.side-product p,
.side-order p {
  margin: 6px 0;
  color: #606b7b;
}

.side-product .side-card-body > span,
.side-order .side-card-body > span {
  color: #f56c6c;
  font-weight: 700;
}

.empty-box {
  min-height: 120px;
  display: grid;
  place-items: center;
  color: #98a2b3;
  text-align: center;
}

@media (max-width: 1400px) {
  .kefu-page {
    grid-template-columns: 300px minmax(380px, 1fr) 280px;
  }
}

@media (max-width: 1200px) {
  .kefu-page {
    grid-template-columns: 280px minmax(340px, 1fr) 260px;
  }
}
</style>
